import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { FlameIcon } from './flame-icon';
import { Loading } from 'components/loading';
import { useAppSelector } from 'utils/hooks';
import { renderPlayersCount } from '../renders';
import { ServerTimeFormat } from 'utils/constants';
import { Header1 } from 'components/layout/headers';
import { renderMoney } from 'components/table/renders';
import { getFilters } from 'models/table-meta/selectors';
import { GroupIcon } from '../daily-deal/icons/group-icon';
import { getCourseSelector } from 'models/course/selectors';
import { pagePath, TeeTimesFilter } from './tee-times-filter';

import {
  TimeBox,
  BestPrice,
  TimeBlock,
  FlexBoxItem,
  RateBoxEmpty,
  RateBoxName,
  RateBoxPrice,
  FilterTitle,
  PlayersBlock,
  RateContainer,
  FlexBoxContainer,
  RateBoxButton,
} from './elements';

import type { ITimeBlockFilter } from './types';
import type { IRate } from 'modules/api-requests/rates/types';
import type { ITableTeeTime } from 'modules/api-requests/tee-time/types';
import { MobileRateDescription } from './mobile-rate-description';

interface IMobileTable {
  teeTimes?: ITableTeeTime[];
  rates?: IRate[];
  loading?: boolean;
}

const toNumber = (input: string) => parseInt(input.replace(':', ''), 10);

export const MobileTable = ({ teeTimes = [], rates = [], loading }: IMobileTable) => {
  const currentCourse = useAppSelector(getCourseSelector);
  const getFiltersFunc = useSelector(getFilters);
  const timeIntervalFilter = getFiltersFunc(pagePath) as ITimeBlockFilter;
  const [activeRate, setActiveTeeTime] = useState<string | null>(null);
  const filteredTeeTimes = useMemo(() => {
    const interval = timeIntervalFilter?.interval;
    if (interval) {
      return teeTimes.filter((teeTime) => {
        return (
          toNumber(teeTime.startTime) >= toNumber(interval.start) &&
          toNumber(teeTime.startTime) <= toNumber(interval.end)
        );
      });
    } else {
      return teeTimes;
    }
  }, [timeIntervalFilter, teeTimes]);

  return (
    <>
      <FilterTitle>Filter tee times:</FilterTitle>
      <TeeTimesFilter value={timeIntervalFilter} />

      {loading ? (
        <Loading />
      ) : (
        <FlexBoxContainer>
          {filteredTeeTimes?.map((teeTime) => {
            const timeMoment = moment(teeTime.startTime, ServerTimeFormat);

            const teeTimeRates = rates.map((item) => ({
              id: item.id,
              name: item.name,
              teeId: teeTime.id,
              date: teeTime.date,
              time: teeTime.startTime,
              price: teeTime.rates[item.id]?.price,
              description: item.description,
              options: item.options,
            }));

            const isBestPrice = teeTime?.freeSlots === 1 && currentCourse?.bestPriceActive;
            const lastRates = teeTimeRates.length
              ? teeTimeRates.length % 2 === 0
                ? [teeTimeRates[teeTimeRates.length - 2], teeTimeRates[teeTimeRates.length - 1]]
                : [teeTimeRates[teeTimeRates.length - 1]]
              : [];

            return (
              <FlexBoxItem key={teeTime.id}>
                <TimeBox>
                  <TimeBlock>
                    <Header1>{timeMoment.format('hh:mm')}</Header1>
                    <span>{timeMoment.format('A')}</span>
                  </TimeBlock>

                  <PlayersBlock>
                    <GroupIcon /> {renderPlayersCount(teeTime.freeSlots)} players
                  </PlayersBlock>

                  {isBestPrice ? (
                    <BestPrice>
                      <FlameIcon />
                      Best price
                    </BestPrice>
                  ) : null}
                </TimeBox>

                <RateContainer>
                  {teeTimeRates.map(({ id, name, price }, index) => {
                    const hasPrice = Boolean(price) || price === 0;
                    const currentRowRates =
                      index > 0 && index % 2 === 0 ? [teeTimeRates[index - 2], teeTimeRates[index - 1]] : [];

                    return (
                      <>
                        {currentRowRates.map((rate) => (
                          <MobileRateDescription
                            key={rate.id}
                            activeKey={activeRate}
                            rate={rate}
                          ></MobileRateDescription>
                        ))}
                        <RateBoxButton
                          key={id}
                          onClick={() => setActiveTeeTime(teeTime.id + id)}
                          className={activeRate === teeTime.id + id ? 'active' : ''}
                        >
                          <RateBoxName>{name}</RateBoxName>
                          <RateBoxPrice>{hasPrice ? renderMoney(price || 0) : 'N/A'}</RateBoxPrice>
                        </RateBoxButton>
                      </>
                    );
                  })}
                  {teeTimeRates.length % 2 !== 0 ? <RateBoxEmpty /> : null}
                  {lastRates.map((rate) => (
                    <MobileRateDescription key={rate.id} activeKey={activeRate} rate={rate}></MobileRateDescription>
                  ))}
                </RateContainer>
              </FlexBoxItem>
            );
          })}
        </FlexBoxContainer>
      )}
    </>
  );
};

